import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { CrosshairSimple, Lightning, Scales, Shield, Target, FileText, ArrowRight, Warning } from "@phosphor-icons/react";

// Primary paid CTA — Gumroad checkout (opens in new tab, secure rel attrs)
const CHECKOUT_URL = "https://eaglefish.gumroad.com/l/txtqey";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/65306284-f0b7-4b09-9326-a0975e697622/images/3c5c9231cdecfda9022a6ef7f64cd3c50e72012d11bd98209c217a63f25cf861.png";
const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/65306284-f0b7-4b09-9326-a0975e697622/images/f247fcede9b537cb41f168dbba7e0020704ecb25bb278c008bc2751920bcba9c.png";
const DOCS_BG = "https://static.prod-images.emergentagent.com/jobs/65306284-f0b7-4b09-9326-a0975e697622/images/e186a3299b85ef21137319f64c6f38a50087fbf2b37684fa3254c72a803992e6.png";

const MARQUEE = ["DEMAND LETTER", "CFPB COMPLAINT", "FTC FILING", "STATE AG ESCALATION", "FDCPA VIOLATION", "FCRA DISPUTE", "EXECUTIVE LETTER", "BBB REPORT", "CEASE & DESIST", "DISCOVERY REQUEST"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#1A1A1A]" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 grid-overlay opacity-40" />

        {/* Marquee */}
        <div className="relative z-10 border-b border-[#1A1A1A] py-3 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap gap-12 font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366]/80">
            {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={`${m}-${i}`} className="flex items-center gap-12">
                <span>{m}</span>
                <span className="text-neutral-700">/</span>
              </span>
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-32">
          <div className="flex items-center gap-3 mb-8 fade-in-up" data-testid="hero-tag">
            <Warning size={16} weight="fill" className="text-[#FF3366]" />
            <span className="font-mono-sf text-xs uppercase tracking-[0.3em] text-neutral-400">
              The Corporation Automates. So Should You.
            </span>
          </div>

          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-8xl lg:text-[9rem] leading-[0.85] mb-10 fade-in-up max-w-5xl" data-testid="hero-headline">
            Fight back<br />
            at <span className="text-[#FF3366]">corporate</span><br />
            speed.
          </h1>

          <p className="max-w-2xl text-lg md:text-xl text-neutral-300 leading-relaxed mb-6 fade-in-up" data-testid="hero-subheadline">
            They use AI to ruin your credit, deny your housing, and shut off your gate codes
            with a single keystroke. <span className="text-white font-semibold">Systemic Friction</span> arms
            you with the same automation, hyper-targeted legal and regulatory firepower &mdash;
            launched in seconds, not months.
          </p>

          {/* Pricing badge */}
          <div className="inline-flex items-center gap-3 border border-[#00FFCC] px-4 py-2 mb-8 fade-in-up" data-testid="pricing-badge">
            <span className="font-heading font-black text-[#00FFCC] text-lg tracking-tight">$97</span>
            <span className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-neutral-300">
              One-Time &middot; Full Legal Arsenal
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 fade-in-up">
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-3 h-14 px-8 bg-[#FF3366] hover:bg-[#FF5248] text-white font-mono-sf text-sm uppercase tracking-[0.25em] font-bold transition-colors"
              data-testid="hero-cta-primary"
            >
              Deploy a Case
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/login"
              className="flex items-center justify-center gap-3 h-14 px-8 border border-[#1A1A1A] hover:border-white text-white font-mono-sf text-sm uppercase tracking-[0.25em] transition-colors"
              data-testid="hero-cta-secondary"
            >
              Login to Command Center
            </Link>
          </div>

          {/* Transparency statement */}
          <div
            className="mt-10 border-l-2 border-[#00FFCC] pl-4 py-3 max-w-2xl fade-in-up"
            data-testid="hero-transparency"
          >
            <div className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-[#00FFCC] mb-2">
              // transparency
            </div>
            <p className="font-mono-sf text-xs leading-relaxed text-neutral-300">
              Independently owned &amp; operated by a single individual consumer. Not a corporation.
              No affiliation with any credit agency, lender, debt collector, or financial institution.
              No referral fees or commissions from any third party.
            </p>
          </div>

          {/* Stats strip */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 border border-[#1A1A1A] divide-x divide-[#1A1A1A]">
            {[
              { v: "06", l: "Filing Targets" },
              { v: "<60s", l: "Avg Draft Time" },
              { v: "24/7", l: "Adversarial Mode" },
              { v: "$0", l: "Per Filing" },
            ].map((s, i) => (
              <div key={s.l} className="p-6 md:p-8" data-testid={`hero-stat-${i}`}>
                <div className="font-mono-sf text-3xl md:text-4xl text-[#FF3366] font-bold">{s.v}</div>
                <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Asymmetry */}
      <section className="border-b border-[#1A1A1A]" data-testid="asymmetry-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366] mb-4">// 01 — The Asymmetry</div>
              <h2 className="font-heading font-black uppercase tracking-tight text-4xl md:text-5xl leading-none">
                One keystroke<br />vs. one lifetime.
              </h2>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-2 gap-px bg-[#1A1A1A]">
              {[
                { t: "THEY", c: "Automated AI engines", d: "Credit denial in 0.3s. Housing screening in 4ms. Gate code revoked instantly." },
                { t: "YOU", c: "Manual bureaucratic loops", d: "Hold music. Faxed forms. 60-day review cycles. Burnout." },
                { t: "THEY", c: "Scaled enforcement", d: "Same form letter to 4 million consumers. Zero marginal cost." },
                { t: "YOU", c: "One-on-one combat", d: "Each agency. Each form. Each rejection. Each retry." },
              ].map((row, i) => (
                <div key={`${row.t}-${row.c}`} className="bg-[#0D0D0D] p-6 md:p-8" data-testid={`asym-row-${i}`}>
                  <div className={`font-mono-sf text-xs uppercase tracking-[0.3em] mb-3 ${row.t === "THEY" ? "text-neutral-500" : "text-[#FF3366]"}`}>{row.t}</div>
                  <div className="font-heading font-bold uppercase text-xl mb-2">{row.c}</div>
                  <p className="text-sm text-neutral-400">{row.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-[#1A1A1A] relative" data-testid="how-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${TEXTURE_BG})` }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366] mb-4">// 02 — The Counter-Strike</div>
          <h2 className="font-heading font-black uppercase tracking-tight text-4xl md:text-6xl leading-none mb-16 max-w-3xl">
            Match their tempo.<br />Drown them in paper.
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
            {[
              { n: "01", i: Target, t: "Identify Adversary", d: "Name the corporation. Pick the harm: credit, billing, housing, shutoffs, harassment." },
              { n: "02", i: Lightning, t: "AI Drafts Six Fronts", d: "Claude Sonnet 4.5 generates a Demand Letter + CFPB, FTC, State AG, BBB, and Executive escalation in seconds." },
              { n: "03", i: Scales, t: "Launch & Escalate", d: "Download, edit, and fire. Log every action. The corporation now has six fronts to defend." },
            ].map((s, i) => {
              const Icon = s.i;
              return (
                <div key={s.n} className="bg-[#0D0D0D] p-8 md:p-10" data-testid={`how-step-${i}`}>
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono-sf text-sm text-neutral-600 tracking-[0.2em]">{s.n}</span>
                    <Icon size={28} weight="bold" className="text-[#FF3366]" />
                  </div>
                  <h3 className="font-heading font-bold uppercase text-2xl tracking-tight mb-3">{s.t}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Arsenal */}
      <section className="border-b border-[#1A1A1A]" data-testid="arsenal-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366] mb-4">// 03 — The Arsenal</div>
              <h2 className="font-heading font-black uppercase tracking-tight text-4xl md:text-5xl leading-none mb-6">
                Every front,<br />armed.
              </h2>
              <p className="text-neutral-400 text-base mb-8">
                Each case automatically generates targeted documents for the agencies and channels
                that corporations actually fear.
              </p>
              <div
                className="aspect-[4/3] bg-cover bg-center border border-[#1A1A1A]"
                style={{ backgroundImage: `url(${DOCS_BG})` }}
                data-testid="arsenal-image"
              />
            </div>
            <div className="md:col-span-7 grid grid-cols-2 gap-px bg-[#1A1A1A] border border-[#1A1A1A] self-start">
              {[
                { s: "DEMAND", t: "Legal Demand Letter", d: "FDCPA / FCRA / UDAAP citations. 14-day clock." },
                { s: "CFPB", t: "CFPB Complaint", d: "Federal financial regulator filing narrative." },
                { s: "FTC", t: "FTC Fraud Report", d: "ReportFraud.ftc.gov compliant submission." },
                { s: "AG", t: "State AG Complaint", d: "Attorney General Consumer Protection Division." },
                { s: "BBB", t: "BBB Filing", d: "Public-facing reputational pressure." },
                { s: "EXEC", t: "CEO Escalation", d: "C-suite & General Counsel direct strike." },
              ].map((a) => (
                <div key={a.s} className="bg-[#0D0D0D] p-5 md:p-6 hover:bg-[#1A1A1A] transition-colors" data-testid={`arsenal-${a.s.toLowerCase()}`}>
                  <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#FF3366] mb-2">{a.s}</div>
                  <div className="font-heading font-bold uppercase text-lg leading-tight mb-1">{a.t}</div>
                  <div className="text-xs text-neutral-500 leading-relaxed">{a.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[#1A1A1A] bg-[#1A1A1A]" data-testid="final-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
          <FileText size={48} weight="bold" className="text-[#FF3366] mx-auto mb-8" />
          <h2 className="font-heading font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.9] mb-6">
            Stop fighting fair.
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10 text-lg">
            They never did. Deploy your first case in under five minutes.
          </p>
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 h-14 px-10 bg-[#FF3366] hover:bg-[#FF5248] text-white font-mono-sf text-sm uppercase tracking-[0.25em] font-bold transition-colors"
            data-testid="footer-cta-register"
          >
            Deploy Now <ArrowRight size={18} weight="bold" />
          </a>
        </div>
      </section>

      <footer className="py-10 px-6 md:px-10 max-w-[1400px] mx-auto space-y-6" data-testid="footer">
        {/* Transparency statement */}
        <div
          className="border-t border-[#1A1A1A] pt-8 grid md:grid-cols-[auto_1fr] gap-4 md:gap-8"
          data-testid="footer-transparency"
        >
          <div className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-[#00FFCC]">
            // transparency
          </div>
          <p className="font-mono-sf text-xs leading-relaxed text-neutral-400 max-w-3xl">
            Systemic Friction is independently owned and operated by a single individual consumer.
            It is <span className="text-white font-semibold">not</span> a corporation, and has
            <span className="text-white font-semibold"> no affiliation</span> with any credit
            agency, lender, debt collector, or financial institution. The platform receives
            <span className="text-white font-semibold"> no referral fees or commissions</span> from
            any third party. All draft output is the consumer&apos;s own work-product, generated
            with AI assistance and reviewed by the consumer before use.
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-[#1A1A1A] pt-6">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#FF3366] flex items-center justify-center">
              <CrosshairSimple size={16} weight="bold" color="#fff" />
            </div>
            <span className="font-mono-sf text-xs uppercase tracking-[0.3em] text-neutral-500">Systemic Friction &copy; 2026</span>
          </div>
          <p className="text-xs text-neutral-600 font-mono-sf uppercase tracking-wider max-w-xl">
            Not a substitute for licensed legal counsel. Drafts are AI-generated &mdash; review before sending.
          </p>
        </div>
      </footer>
    </div>
  );
}
